import * as React from "react";
import { useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import styled, { css } from "styled-components";
import { RequiredMarker, Select, Spinner, Icon, Tooltip } from "components/common";
import { Button, ButtonToolbar, ControlLabel, HelpBlock } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useModelProviders from "ai-management/hooks/useModelProviders";
import useModels from "ai-management/hooks/useModels";
import useModelProviderMutations from "ai-management/hooks/useModelProviderMutations";
import useSaveModelProvider from "ai-management/model-providers/hooks/useSaveModelProvider";
import useConfiguredModelProviders from "ai-management/model-providers/hooks/useConfiguredModelProviders";
import ProviderCredentialFields from "ai-management/components/ProviderCredentialFields";
import IGNORE_AUTOFILL from "ai-management/components/passwordManagerAutofill";
import { emptyAuthFor, toFormAuth } from "ai-management/providerAuth";
import FormGroup from "components/bootstrap/FormGroup";
import UsedByFeatures from "ai-management/model-providers/form/UsedByFeatures";
const secretUsable = (value) => !!(value && (value.set_value || value.keep_value));
const credentialsOf = (values) => {
  var _a;
  return (_a = values.credentials) != null ? _a : { provider: values.provider };
};
const isOllama = (values) => {
  var _a;
  return ((_a = credentialsOf(values).auth) == null ? void 0 : _a.type) === "ollama";
};
const ollamaBlocker = (values) => {
  const auth = credentialsOf(values).auth;
  if ((auth == null ? void 0 : auth.type) !== "ollama") {
    return null;
  }
  if (!auth.base_url) {
    return "Enter the base URL of your Ollama server.";
  }
  if (values.baseUrlAllowlisted === void 0) {
    return "Checking the base URL against the URL allowlist\u2026";
  }
  return values.baseUrlAllowlisted ? null : "Add the base URL to the URL allowlist.";
};
const modelPlaceholder = (values, loading, credentials) => {
  if (!values.provider) {
    return "Select a provider first";
  }
  if (!credentials) {
    return isOllama(values) ? "Enter an allowlisted base URL to load models" : "Enter credentials to load models";
  }
  return loading ? "Loading models\u2026" : "Select a default model";
};
const modelHelp = (values, loaded) => {
  var _a;
  if (isOllama(values)) {
    return (_a = ollamaBlocker(values)) != null ? _a : "Models this Ollama has pulled. An empty list means it has none.";
  }
  return loaded ? "Models this account can use, as reported by the provider. AI features use this model unless you choose a different one in their configuration." : "Enter credentials above to load the models available to your account.";
};
const hasUsableCredential = (values) => {
  const auth = credentialsOf(values).auth;
  switch (auth == null ? void 0 : auth.type) {
    case "api_key":
      return secretUsable(auth.api_key);
    case "bedrock_api_token":
      return secretUsable(auth.token) && !!auth.region;
    case "bedrock_aws_credentials":
      return secretUsable(auth.access_key_id) && secretUsable(auth.secret_key) && !!auth.region;
    // Ollama has no secret at all: the URL is the whole configuration, and it has to be allowlisted.
    case "ollama":
      return ollamaBlocker(values) === null;
    default:
      return false;
  }
};
const saveBlockedReason = (values) => {
  var _a;
  const auth = credentialsOf(values).auth;
  if (!values.provider) {
    return "Select a provider.";
  }
  if ((auth == null ? void 0 : auth.type) === "bedrock_aws_credentials" && secretUsable(auth.access_key_id) !== secretUsable(auth.secret_key)) {
    return "Enter both the access key ID and the secret access key, or clear both.";
  }
  if (((auth == null ? void 0 : auth.type) === "bedrock_aws_credentials" || (auth == null ? void 0 : auth.type) === "bedrock_api_token") && !auth.region) {
    return "Select an AWS region. Bedrock model lists and endpoints are specific to one region.";
  }
  if (!hasUsableCredential(values)) {
    return (_a = ollamaBlocker(values)) != null ? _a : "Enter credentials for this provider.";
  }
  if (!values.default_model_id) {
    return "Select a default model.";
  }
  return null;
};
const ErrorBlock = styled.pre`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
const TestStatus = styled.span`
  margin-left: ${({ theme }) => theme.spacings.sm};
`;
const SuccessIcon = styled(Icon)(
  ({ theme }) => `
  color: ${theme.colors.variant.success};
`
);
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const SaveHintIcon = styled(Icon)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xxs};
  `
);
const AlreadyConfiguredHint = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
  `
);
const toProviderOptions = (providers, configuredProviders, initialProvider) => providers.map(({ id, display }) => ({
  value: id,
  label: display,
  disabled: id !== (initialProvider == null ? void 0 : initialProvider.provider) && configuredProviders.some(({ provider }) => provider === id)
})).sort((a, b) => a.label.localeCompare(b.label));
const providerOptionRenderer = ({ label, disabled }) => /* @__PURE__ */ React.createElement("span", null, label, disabled && /* @__PURE__ */ React.createElement(AlreadyConfiguredHint, null, " (already configured)"));
const FormContents = ({
  providerOptions,
  initialProvider,
  onCancel = void 0,
  usedBy = void 0,
  deleteSection = void 0
}) => {
  const sendTelemetry = useSendTelemetry();
  const { test } = useModelProviderMutations();
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const credentialsForLookup = hasUsableCredential(values) ? credentialsOf(values) : void 0;
  const {
    data: models,
    isFetching: modelsLoading,
    error: modelsError
  } = useModels(values.provider, credentialsForLookup);
  const [testResult, setTestResult] = useState(null);
  const testPayload = useMemo(
    () => ({
      provider: values.provider,
      model_id: values.default_model_id,
      credentials: values.credentials
    }),
    [values.provider, values.default_model_id, values.credentials]
  );
  const storedAuth = initialProvider == null ? void 0 : initialProvider.auth;
  const isEditing = !!initialProvider;
  const submitButtonText = isEditing ? "Update model provider" : "Create model provider";
  const submitLoadingText = isEditing ? "Updating model provider..." : "Creating model provider...";
  const onProviderChange = (provider) => {
    setFieldValue("provider", provider);
    setFieldValue("credentials", { provider, auth: emptyAuthFor(provider) });
    setFieldValue("default_model_id", "");
    setFieldValue("baseUrlAllowlisted", void 0);
    setTestResult(null);
  };
  const onTest = () => {
    setTestResult(null);
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_MANAGEMENT.MODEL_PROVIDER_TEST_CLICKED, {
      app_section: "ai-management-model-providers",
      event_details: { provider: values.provider }
    });
    test.mutateAsync(testPayload).then((result) => setTestResult(result)).catch((error) => setTestResult({ success: false, message: String(error) }));
  };
  const saveHint = saveBlockedReason(values);
  const incomplete = !!saveHint;
  const testDisabled = incomplete || test.isPending;
  const submitDisabled = incomplete;
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "provider" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Provider", /* @__PURE__ */ React.createElement(RequiredMarker, null)), /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "provider",
      placeholder: "Select a provider",
      options: providerOptions,
      optionRenderer: providerOptionRenderer,
      value: values.provider,
      onChange: onProviderChange,
      disabled: isEditing,
      clearable: false,
      inputProps: IGNORE_AUTOFILL
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "The provider that offers the models, such as OpenAI or Anthropic.")), values.provider && /* @__PURE__ */ React.createElement(ProviderCredentialFields, { key: values.provider, provider: values.provider, storedAuth }), values.provider && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "model" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Default model", /* @__PURE__ */ React.createElement(RequiredMarker, null)), /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "model",
      placeholder: modelPlaceholder(values, modelsLoading, credentialsForLookup),
      options: models.map((model) => ({ value: model.id, label: model.name })),
      value: values.default_model_id,
      onChange: (modelId) => {
        setFieldValue("default_model_id", modelId);
        setTestResult(null);
      },
      disabled: !values.provider || !credentialsForLookup || modelsLoading || models.length === 0,
      inputProps: IGNORE_AUTOFILL
    }
  ), modelsError ? (
    // The provider refused or could not be reached. Shown rather than swallowed: an empty
    // dropdown with no explanation reads as "no models supported", which is never the reason.
    /* @__PURE__ */ React.createElement(ErrorBlock, null, modelsError.message)
  ) : /* @__PURE__ */ React.createElement(HelpBlock, null, modelHelp(values, !!credentialsForLookup))), values.provider && usedBy && /* @__PURE__ */ React.createElement(UsedByFeatures, { ...usedBy }), testResult && !testResult.success && /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Test error"), /* @__PURE__ */ React.createElement(ErrorBlock, null, testResult.message)), deleteSection, /* @__PURE__ */ React.createElement(Actions, null, /* @__PURE__ */ React.createElement(ButtonToolbar, null, onCancel && /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: onCancel, disabled: isSubmitting }, "Cancel"), /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: onTest, disabled: testDisabled }, test.isPending ? /* @__PURE__ */ React.createElement(Spinner, { delay: 0, text: "Testing" }) : "Test", (testResult == null ? void 0 : testResult.success) && /* @__PURE__ */ React.createElement(TestStatus, null, /* @__PURE__ */ React.createElement(SuccessIcon, { name: "check_circle" }))), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      type: "submit",
      "aria-label": submitButtonText,
      disabled: submitDisabled || isSubmitting
    },
    isSubmitting ? /* @__PURE__ */ React.createElement(Spinner, { text: submitLoadingText, delay: 0 }) : /* @__PURE__ */ React.createElement(React.Fragment, null, submitButtonText, saveHint && /* @__PURE__ */ React.createElement(Tooltip, { label: saveHint, position: "top" }, /* @__PURE__ */ React.createElement(SaveHintIcon, { name: "info", "data-testid": "save-hint" })))
  ))));
};
const ModelProviderForm = ({
  initialProvider = void 0,
  onSaved = void 0,
  onCancel = void 0,
  usedBy = void 0,
  deleteSection = void 0
}) => {
  var _a;
  const { data: providers } = useModelProviders();
  const { data: configuredProviders } = useConfiguredModelProviders();
  const { saveProvider } = useSaveModelProvider();
  const provider = initialProvider == null ? void 0 : initialProvider.provider;
  const initialValues = {
    provider,
    default_model_id: (_a = initialProvider == null ? void 0 : initialProvider.default_model_id) != null ? _a : "",
    // The stored auth object, so the form starts on the method the operator chose. Secrets are managed by
    // the credential fields via their is_set flags and are never sent back as plaintext.
    credentials: {
      provider,
      auth: (initialProvider == null ? void 0 : initialProvider.auth) ? toFormAuth(initialProvider.auth) : emptyAuthFor(provider)
    },
    baseUrlAllowlisted: void 0
  };
  const onSubmit = (values) => {
    var _a2;
    return saveProvider({
      id: initialProvider == null ? void 0 : initialProvider.id,
      provider: values.provider,
      auth: (_a2 = credentialsOf(values).auth) != null ? _a2 : emptyAuthFor(values.provider),
      default_model_id: values.default_model_id
    }).then((savedProvider) => onSaved == null ? void 0 : onSaved(savedProvider.id)).catch(() => {
    });
  };
  return /* @__PURE__ */ React.createElement(Formik, { initialValues, enableReinitialize: true, onSubmit }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(
    FormContents,
    {
      providerOptions: toProviderOptions(providers, configuredProviders, initialProvider),
      initialProvider,
      onCancel,
      usedBy,
      deleteSection
    }
  )));
};
export default ModelProviderForm;
